import { useState } from 'react'
import {
  Users, Percent, Award, Copy, CheckCircle, Gift
} from 'lucide-react'
import type { User as BaseUser } from '../../lib/types'

type ReferralUser = BaseUser & {
  referral_code?: string
  referred_by?: string
  referrer_id?: string | null
  referral_count?: number
}

interface ReferralStats {
  totalReferrals: number
  monthlyEarnings: number
  totalEarnings: number
}

interface ReferredUser {
  id: string
  email: string
  full_name?: string
  created_at: string
}

interface ReferralTabProps {
  user: ReferralUser
  referralStats: ReferralStats
  referredUsers: ReferredUser[]
  loading: boolean
}

export default function ReferralTab({ user, referralStats, referredUsers, loading }: ReferralTabProps) {
  const [copiedReferralCode, setCopiedReferralCode] = useState(false)
  const [copiedReferralLink, setCopiedReferralLink] = useState(false)
  const referralCode = user.referral_code || user.id || ''
  const referredBy = user.referrer_id || user.referred_by
  const inheritedDepositPercent = 15
  const userDiscountCapPercent = 5

  const getReferralLink = () => {
    const refValue = referralCode
    if (!refValue) return ''
    const key = user.referral_code ? 'ref' : 'referrer_id'
    return `${window.location.origin}?${key}=${refValue}`
  }

  const copyReferral = (text: string, type: 'code' | 'link') => {
    navigator.clipboard.writeText(text).then(() => {
      if (type === 'code') {
        setCopiedReferralCode(true)
        setTimeout(() => setCopiedReferralCode(false), 2000)
      } else {
        setCopiedReferralLink(true)
        setTimeout(() => setCopiedReferralLink(false), 2000)
      }
    })
  }



  // Fetch referrer name only if needed
  /* Removed as per user request
  (đã gỡ logic fetch trực tiếp từ DB)
  */

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-600"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-gradient-to-br from-amber-500 to-amber-600 rounded-lg shadow-sm p-4 text-white">
          <div className="flex items-center justify-between mb-2">
            <Users className="h-6 w-6 opacity-80" />
          </div>
          <div className="text-sm opacity-90 mb-1">Tổng giới thiệu</div>
          <div className="text-2xl font-bold">{referralStats.totalReferrals}</div>
        </div>

        <div className="bg-gradient-to-br from-yellow-500 to-yellow-600 rounded-lg shadow-sm p-4 text-white">
          <div className="flex items-center justify-between mb-2">
            <Percent className="h-6 w-6 opacity-80" />
          </div>
          <div className="text-sm opacity-90 mb-1">Mời người đầu tiên</div>
          <div className="text-2xl font-bold">{referralStats.totalReferrals > 0 ? 'Đã đạt' : 'Chưa đạt'}</div>
          {referralStats.totalReferrals === 0 && (
            <div className="text-xs opacity-75 mt-1">
              Hãy mời ít nhất 1 người để bắt đầu chuỗi giới thiệu
            </div>
          )}
        </div>
      </div>

      {/* Referral Info for User (Who referred me?) */}
      {referredBy && (
        <div className="bg-amber-50 dark:bg-blue-950/20 rounded-lg border border-amber-100 dark:border-blue-900/40 p-4">
          <div className="flex items-start gap-3">
            <div className="p-2 bg-amber-100 dark:bg-blue-900/30 rounded-full">
              <Gift className="h-5 w-5 text-amber-600" />
            </div>
            <div>
              <h3 className="font-semibold text-blue-900 dark:text-blue-200 mb-1">Bạn đã được giới thiệu!</h3>
              <p className="text-sm text-amber-700 dark:text-blue-300">
                Tài khoản của bạn đang kế thừa <span className="font-bold">{inheritedDepositPercent}% tổng nạp</span> của người mời để hỗ trợ thăng hạng nhanh hơn.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* User Rank Section */}
      <div className="bg-gray-50 dark:bg-gray-950/40 rounded-lg border border-gray-200 dark:border-gray-800 p-6">
        <h2 className="text-lg font-semibold mb-4 flex items-center">
          <Award className="h-5 w-5 mr-2 text-yellow-600" />
          Hạng thành viên
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg p-4">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center">
                <Award className="h-6 w-6 text-yellow-600" />
              </div>
              <div>
                <h3 className="font-semibold text-lg capitalize">
                  {user.rank === 'dong' ? 'Đồng' :
                    (user.rank === 'bac' || user.rank === 'sat') ? 'Bạc' :
                      user.rank === 'vang' ? 'Vàng' :
                        user.rank === 'luc_bao' ? 'Lục bảo' :
                          user.rank === 'kim_cuong' ? 'Kim cương' : 'Tân binh'}
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-300">Hạng hiện tại</p>
              </div>
            </div>

            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-300">Giảm giá hạng:</span>
                <span className="font-medium text-green-600">
                  {user.rank === 'dong' ? '1%' :
                    (user.rank === 'bac' || user.rank === 'sat') ? '2%' :
                      user.rank === 'vang' ? '3%' :
                        user.rank === 'luc_bao' ? '4%' :
                          user.rank === 'kim_cuong' ? '5%' : '0%'}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-300">Trần giảm giá cá nhân:</span>
                <span className="font-medium text-amber-600">{userDiscountCapPercent}%</span>
              </div>
              {referredBy && (
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-300">Kế thừa tổng nạp:</span>
                  <span className="font-medium text-green-600">{inheritedDepositPercent}%</span>
                </div>
              )}
              <div className="flex justify-between pt-2 border-t mt-2">
                <span className="text-gray-600 dark:text-gray-300 font-medium">Mục tiêu mời đầu tiên:</span>
                <span className="font-bold text-amber-600 bg-amber-50 dark:bg-blue-950/30 px-2 py-0.5 rounded">
                  {referralStats.totalReferrals > 0 ? 'Đã hoàn thành' : 'Chưa có lượt mời'}
                </span>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg p-4">
            <h4 className="font-medium mb-3">Cấp độ tiếp theo</h4>
            <div className="space-y-3">
              {(() => {
                const currentDeposited = user.total_deposited || 0
                let nextMilestone = ''
                let neededAmount = 0
                let currentProgress = 0

                if (user.rank === 'kim_cuong') {
                  return <p className="text-sm text-gray-600 dark:text-gray-300 text-center py-2">Bạn đã đạt hạng cao nhất (Kim Cương)!</p>
                }

                if (currentDeposited < 500000) {
                  nextMilestone = 'Đồng (500K)'
                  neededAmount = 500000 - currentDeposited
                  currentProgress = currentDeposited / 500000
                } else if (currentDeposited < 2000000) {
                  nextMilestone = 'Bạc (2M)'
                  neededAmount = 2000000 - currentDeposited
                  currentProgress = (currentDeposited - 500000) / 1500000
                } else if (currentDeposited < 10000000) {
                  nextMilestone = 'Vàng (10M)'
                  neededAmount = 10000000 - currentDeposited
                  currentProgress = (currentDeposited - 2000000) / 8000000
                } else if (currentDeposited < 50000000) {
                  nextMilestone = 'Lục bảo (50M)'
                  neededAmount = 50000000 - currentDeposited
                  currentProgress = (currentDeposited - 10000000) / 40000000
                } else if (currentDeposited < 200000000) {
                  nextMilestone = 'Kim cương (200M)'
                  neededAmount = 200000000 - currentDeposited
                  currentProgress = (currentDeposited - 50000000) / 150000000
                } else {
                  return <p className="text-sm text-gray-600 dark:text-gray-300 text-center py-2">Bạn đã đạt hạng cao nhất!</p>
                }

                return (
                  <div>
                    <p className="text-sm text-gray-600 dark:text-gray-300 mb-2">
                      Cần nạp thêm <span className="font-medium text-amber-600">{neededAmount.toLocaleString('vi-VN')}đ</span> để đạt <span className="font-medium">{nextMilestone}</span>
                    </p>
                    <div className="w-full bg-gray-200 dark:bg-gray-800 rounded-full h-2">
                      <div
                        className="bg-amber-600 h-2 rounded-full transition-all duration-300"
                        style={{ width: `${Math.min(currentProgress * 100, 100)}%` }}
                      ></div>
                    </div>
                  </div>
                )
              })()}
            </div>
          </div>
        </div>
      </div>

      {/* Referral Link Section */}
      <div className="bg-white dark:bg-gray-900 rounded-lg border border-amber-100 dark:border-blue-900/40 p-6">
        <h2 className="text-lg font-semibold mb-4 flex items-center">
          <Copy className="h-5 w-5 mr-2 text-amber-600" />
          Link giới thiệu của bạn
        </h2>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Mã giới thiệu:</label>
            <div className="flex gap-2">
              <input
                type="text"
                value={referralCode}
                readOnly
                className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-700 rounded-md bg-gray-50 dark:bg-gray-900 font-mono font-semibold"
              />
              <button
                onClick={() => copyReferral(referralCode, 'code')}
                className="px-4 py-2 bg-amber-600 text-white rounded-md hover:bg-amber-700 transition-colors flex items-center gap-2"
              >
                {copiedReferralCode ? (
                  <><CheckCircle className="h-4 w-4" /><span>Đã sao chép</span></>
                ) : (
                  <><Copy className="h-4 w-4" /><span>Sao chép</span></>
                )}
              </button>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Link giới thiệu:</label>
            <div className="flex gap-2">
              <input
                type="text"
                value={getReferralLink()}
                readOnly
                className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-700 rounded-md bg-gray-50 dark:bg-gray-900 text-sm overflow-ellipsis"
              />
              <button
                onClick={() => copyReferral(getReferralLink(), 'link')}
                className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors flex items-center gap-2"
              >
                {copiedReferralLink ? (
                  <><CheckCircle className="h-4 w-4" /><span>Đã sao chép</span></>
                ) : (
                  <><Copy className="h-4 w-4" /><span>Sao chép</span></>
                )}
              </button>
            </div>
          </div>
        </div>

        <div className="mt-6 p-4 bg-yellow-50 dark:bg-yellow-950/20 rounded-lg border border-yellow-200 dark:border-yellow-900/40">
          <h3 className="font-semibold text-yellow-800 dark:text-yellow-200 mb-2 flex items-center text-sm">
            <Gift className="h-4 w-4 mr-2" />
            Cách thức hoạt động:
          </h3>
          <ul className="text-sm text-yellow-700 dark:text-yellow-300 space-y-1 ml-6 list-disc">
            <li>Chia sẻ link giới thiệu cho bạn bè</li>
            <li>Khuyến khích mời ít nhất 1 người đầu tiên để bắt đầu hệ thống giới thiệu</li>
            <li>Người được mời kế thừa {inheritedDepositPercent}% tổng nạp của người mời</li>
            <li>Giảm giá cá nhân tối đa toàn hệ thống là {userDiscountCapPercent}%</li>
          </ul>
        </div>
      </div>

      {/* Referred Users */}
      <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-800 p-6">
        <h2 className="text-lg font-semibold mb-4 flex items-center">
          <Users className="h-5 w-5 mr-2 text-purple-600" />
          Người bạn đã giới thiệu {referredUsers.length > 0 && `(${referredUsers.length})`}
        </h2>

        {referredUsers.length === 0 ? (
          <div className="text-center py-8 text-gray-500 dark:text-gray-300 bg-gray-50 dark:bg-gray-950/40 rounded-lg">
            <Users className="h-10 w-10 mx-auto mb-3 text-gray-300 dark:text-gray-600" />
            <p>Chưa có ai đăng ký qua link của bạn</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-800">
              <thead className="bg-gray-50 dark:bg-gray-950/40">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Email</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Ngày đăng ký</th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-800">
                {referredUsers.map((refUser) => (
                  <tr key={refUser.id}>
                    <td className="px-6 py-4 whitespace-normal break-all align-top text-sm text-gray-900 dark:text-gray-100">
                      <div className="flex flex-col">
                        <span className="whitespace-normal break-all">{refUser.email}</span>
                        {refUser.full_name && <span className="text-xs text-gray-500 dark:text-gray-300 whitespace-normal break-words">{refUser.full_name}</span>}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300">
                      {new Date(refUser.created_at).toLocaleDateString('vi-VN')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}





